import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SparklesIcon } from 'lucide-react';
import { Button } from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { MarkdownArticle } from '@/components/MarkdownArticle';
import { visibleReleases } from '@/help/content';
import { useFeatureLookup } from '@/lib/app-features';
import { hasPermission, markReleaseSeen, readMyIdentity } from '@/lib/events';
import { useGuide } from '@/guides/use-guide';

const INTERNAL_ROLES = new Set(['internal', 'admin', 'superadmin']);

/**
 * The popup for a release marked `announce: true`: once per internal user,
 * newest first when several are unseen, gone for good once dismissed.
 */
export function ReleaseAnnouncement(): React.ReactElement | null {
  const queryClient = useQueryClient();
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const runGuide = useGuide();
  const isFeatureOn = useFeatureLookup();
  const seen = useMutation({
    mutationFn: (slug: string) => markReleaseSeen(slug),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-identity'] });
    },
  });

  if (!identity.data || !INTERNAL_ROLES.has(identity.data.role)) return null;
  const alreadySeen = new Set([...(identity.data.seenReleases ?? []), ...dismissed]);
  const release = visibleReleases(isFeatureOn, (permission) => hasPermission(identity.data, permission)).find((candidate) => candidate.announce && !alreadySeen.has(candidate.slug));
  if (!release) return null;

  const dismiss = (): void => {
    setDismissed((current) => [...current, release.slug]);
    seen.mutate(release.slug);
  };

  return (
    <Dialog open onOpenChange={(next) => (!next ? dismiss() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-2xl">
        <DialogTitle className="flex items-center gap-2">
          <SparklesIcon aria-hidden className="size-4 text-brand" /> What's new: {release.title}
        </DialogTitle>
        <p className="text-portal-small text-portal-muted">New in the app since you were last here.</p>
        <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-portal-button border border-portal-line p-4">
          <MarkdownArticle
            markdown={release.markdown}
            onGuide={(id) => {
              dismiss();
              runGuide(id);
            }}
          />
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          <Link to={`/whats-new/${release.slug}`} onClick={dismiss} className="text-portal-small font-semibold text-brand hover:underline">
            Read it on What's new
          </Link>
          <Button onClick={dismiss}>Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
